"use client";

import type { PublicHelpSpace } from "@v1/backend/logic/pages/public_help_space_groups";
import { IconOrEmoji } from "@v1/ui/icon-or-emoji";
import { BookOpen, ChevronRight } from "lucide-react";
import Link from "next/link";
import type { ReactElement } from "react";
import { useHelpHref } from "./help-link-base";

/** Start-page cards for real public knowledge spaces. */
export function HelpSpaceList({
  spaces,
  org_slug,
}: {
  readonly spaces: readonly PublicHelpSpace[];
  readonly org_slug: string;
}): ReactElement {
  const helpHref = useHelpHref(org_slug);
  return (
    <div className="flex flex-col gap-2.5">
      {spaces.map((space) => {
        if (space.pages.length === 0) return null;
        return (
          <Link
            key={space.space_id}
            href={helpHref({
              kind: "space",
              spaceId: space.space_id,
            })}
            prefetch={false}
            className="group flex items-center gap-4 rounded-lg border border-border/60 bg-background px-5 py-4 transition-colors hover:border-border hover:bg-muted/[0.25]"
          >
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted/60 text-[18px] text-primary/55"
              aria-hidden
            >
              {space.icon ? (
                <IconOrEmoji value={space.icon} fallback="" size={18} />
              ) : (
                <BookOpen className="h-[18px] w-[18px]" strokeWidth={1.75} />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px] font-semibold text-primary">
                {space.name}
              </span>
              {space.description ? (
                <span className="mt-0.5 block truncate text-[12.5px] leading-relaxed text-primary/55">
                  {space.description}
                </span>
              ) : null}
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-primary/30 transition-transform group-hover:translate-x-0.5" />
          </Link>
        );
      })}
    </div>
  );
}
